/**
 * Video Prompt Input Component - Glass Morphism Design
 * Text input for video generation with script length validation
 * Validates: Requirements 17.2, 17.3, 18.1
 */
import { useState, useRef, useEffect } from 'react';
import { useVideoStore } from '../../stores/videoStore';
import { Icons } from '../icons';
import { THEME } from '../../styles/theme';

interface VideoPromptInputProps {
  onSubmit: (prompt: string) => void;
  disabled?: boolean;
}

export function VideoPromptInput({ onSubmit, disabled }: VideoPromptInputProps) {
  const [prompt, setPrompt] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const {
    providers,
    selectedProvider,
    selectedModel,
    promptEnhancerEnabled,
    togglePromptEnhancer,
  } = useVideoStore();

  // Get current model's script limit
  const currentProvider = providers.find(p => p.id === selectedProvider);
  const currentModel = currentProvider?.models.find(m => m.id === selectedModel);
  const scriptLimit = currentModel?.scriptLimit || 5000;

  // Calculate remaining characters
  const remainingChars = scriptLimit - prompt.length;
  const isOverLimit = remainingChars < 0;
  const isNearLimit = remainingChars < 500 && remainingChars >= 0;

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 300)}px`;
    }
  }, [prompt]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (prompt.trim() && !isOverLimit && !disabled) {
      onSubmit(prompt.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Input Container */}
      <div
        className="rounded-2xl p-4 border relative overflow-hidden"
        style={{
          backgroundColor: THEME.glassLight,
          backdropFilter: 'blur(10px)',
          borderColor: isOverLimit ? THEME.error : isNearLimit ? THEME.warning : THEME.border,
          boxShadow: THEME.shadowLg,
        }}
      >
        {/* Dot pattern overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.02) 1px, transparent 1px)',
            backgroundSize: '12px 12px',
          }}
        />

        {/* Prompt Input */}
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder="Describe your video... Be specific about scenes, actions, and visual elements."
            className="w-full bg-transparent resize-none border-0 focus:outline-none disabled:opacity-50 min-h-[120px] p-0 pr-20"
            rows={4}
            style={{
              fontSize: '15px',
              lineHeight: '1.6',
              color: THEME.textMain,
            }}
          />

          {/* Character Count */}
          <div
            className="absolute bottom-0 right-0 text-xs px-2 py-1 rounded-lg"
            style={{
              backgroundColor: 'rgba(0,0,0,0.3)',
              color: isOverLimit ? THEME.error : isNearLimit ? THEME.warning : THEME.textMuted,
            }}
          >
            {prompt.length} / {scriptLimit}
          </div>
        </div>

        {/* Script Length Warning */}
        {isOverLimit && (
          <div className="mt-3 relative">
            <p
              className="text-xs flex items-center gap-1"
              style={{ color: THEME.error }}
            >
              <Icons.X size={14} />
              <span>
                Script exceeds {currentModel?.name || 'model'} limit. Consider dividing into multiple parts.
              </span>
            </p>
          </div>
        )}

        {/* Actions Row */}
        <div className="flex items-center justify-between mt-4 relative">
          {/* Prompt Enhancer Toggle */}
          <button
            type="button"
            onClick={togglePromptEnhancer}
            disabled={disabled || !prompt.trim()}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all duration-200 ${
              promptEnhancerEnabled ? 'animate-pulse-glow' : ''
            } disabled:opacity-50`}
            style={{
              borderColor: prompt.trim()
                ? promptEnhancerEnabled
                  ? THEME.accentBorder
                  : THEME.border
                : THEME.border,
              backgroundColor: prompt.trim()
                ? promptEnhancerEnabled
                  ? THEME.accentSoft
                  : 'transparent'
                : 'transparent',
              color: prompt.trim()
                ? promptEnhancerEnabled
                  ? THEME.accentColor
                  : THEME.textMuted
                : THEME.textMuted,
            }}
          >
            <Icons.Sparkles size={16} />
            <span>Enhance</span>
          </button>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={disabled || !prompt.trim() || isOverLimit}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: prompt.trim() && !isOverLimit ? THEME.accent : 'rgba(255,255,255,0.05)',
              color: THEME.textMain,
              boxShadow: prompt.trim() && !isOverLimit ? THEME.shadowAccent : 'none',
            }}
          >
            <Icons.Video size={18} />
            <span>Generate</span>
          </button>
        </div>

        {/* Keyboard Shortcut Hint */}
        <p className="mt-3 text-xs text-right relative" style={{ color: THEME.textMuted }}>
          Press ⌘+Enter to generate
        </p>
      </div>
    </form>
  );
}
